import { US_STATES_LIST } from '../../../constants/constants';
import { OnboardingStatusMap } from '../../FirmBankingView/constants';
import {
  AchDraft,
  CheckDraft,
  PayoutDetailsResponse,
  PayoutFormSource,
  PayoutMethod,
  SettlementPayoutSummary,
} from './types';

export const isPayoutMethod = (value?: string | null): value is PayoutMethod =>
  value === 'ACH' || value === 'CHECK';

export const getSettlementLabel = (settlementId: number, caseReferenceId?: string | null) =>
  [caseReferenceId, `Settlement #${settlementId}`].filter(Boolean).join(' ');

export const getSettlementPath = (settlementId: number) => `/payments/settlement/${settlementId}`;

export const getTextValue =(value: unknown) => (typeof value === 'string' ? value : '');

export const getStateLabel = (value?: string | null) =>
  US_STATES_LIST.find((state) => state.id === value)?.label || value || '';

export const getPayoutStatusChip = (status?: string | null) => {
  const normalizedStatus = status?.toUpperCase();

  switch (normalizedStatus) {
    case 'ACTIVE':
      return { label: 'Active', color: OnboardingStatusMap.COMPLETED.color };
    case 'INACTIVE':
      return { label: 'Inactive', color: OnboardingStatusMap.NOT_STARTED.color };
    case 'PENDING_VERIFICATION':
      return { label: 'Pending Verification', color: OnboardingStatusMap.IN_PROGRESS.color };
    case 'BLOCKED':
      return { label: 'Blocked', color: OnboardingStatusMap.BLOCKED.color };
    default:
      return null;
  }
};

export const isProvisioningErrorStatus = (status?: string | null) => {
  const normalizedStatus = status?.toLowerCase();
  return normalizedStatus === 'criticalerror' || normalizedStatus === 'error';
};

export const isCriticalProvisioningStatus = (status?: string | null) =>
  status?.toLowerCase() === 'criticalerror';

export const getPreferredPayoutMethod = (
  details?: PayoutDetailsResponse | null,
): PayoutMethod | null => {
  const method = details?.preferredPayoutMode?.toUpperCase();
  return isPayoutMethod(method) ? method : null;
};

/** Form source for the client-level payout form (no settlements yet). */
export const toClientFormSource = (details?: PayoutDetailsResponse | null): PayoutFormSource => ({
  achDetails: details?.achDetails ?? null,
  bankAccountStatus: details?.bankAccountStatus ?? null,
  bankAccountPassportIntegration: details?.bankAccountPassportIntegration ?? null,
  mailingAddress: details?.mailingAddress ?? null,
  mailingAddressStatus: details?.mailingAddressStatus ?? null,
  mailingAddressPassportIntegration: details?.mailingAddressPassportIntegration ?? null,
  hasAssignedBankAccount: details?.hasAssignedBankAccount ?? false,
  hasAssignedMailingAddress: details?.hasAssignedMailingAddress ?? false,
  preferredMethod: getPreferredPayoutMethod(details),
});

/**
 * Form source for one settlement's payout form — only that settlement's own details.
 * A voucher ('V') or unset method yields no preferred method, so the form starts empty.
 */
export const toSettlementFormSource = (settlement: SettlementPayoutSummary): PayoutFormSource => {
  const method = settlement.paymentMethod?.toUpperCase();
  return {
    achDetails: settlement.achDetails,
    bankAccountStatus: settlement.bankAccountStatus,
    bankAccountPassportIntegration: settlement.bankAccountPassportIntegration,
    mailingAddress: settlement.mailingAddress,
    mailingAddressStatus: settlement.mailingAddressStatus,
    mailingAddressPassportIntegration: settlement.mailingAddressPassportIntegration,
    hasAssignedBankAccount: false,
    hasAssignedMailingAddress: false,
    preferredMethod: isPayoutMethod(method) ? method : null,
  };
};

export const hasSavedAchDetails =(details?: PayoutDetailsResponse['achDetails'] | null) =>
  Boolean(
    details?.accountHolderName ||
      details?.routingNumber ||
      details?.accountNumber ||
      details?.accountNumberLast4 ||
      details?.hasAccountNumber,
  );

export const hasSavedCheckDetails = (details?: PayoutDetailsResponse['mailingAddress'] | null) =>
  Boolean(details?.line1 || details?.line2 || details?.city || details?.state || details?.zip);

export const getMaskedAccountDisplay = (accountNumber?: string, accountNumberLast4?: string) => {
  const source = accountNumber || accountNumberLast4 || '';
  const digits = source.replace(/\D/g, '');
  const last4 = digits.slice(-4);

  return last4 ? `•••• ${last4}` : '';
};

export const buildAchCreatePayload = (
  draft: AchDraft,
  payoutMethod: PayoutMethod,
  sendEmailNotification: boolean,
  reasonCode?: string,
  note?: string,
) => ({
  accountHolderName: draft.accountHolderName.trim(),
  accountType: draft.accountType,
  routingNumber: draft.routingNumber.trim(),
  accountNumber: draft.accountNumber.trim(),
  payoutMethod,
  sendEmailNotification,
  ...(reasonCode && { reasonCode }),
  ...(note && { note }),
});

export const buildAchUpdatePayload = (
  draft: AchDraft,
  reasonCode: string,
  note: string,
  sendEmailNotification: boolean,
) => ({
  accountHolderName: draft.accountHolderName.trim(),
  accountType: draft.accountType,
  routingNumber: draft.routingNumber.trim(),
  ...(draft.accountNumber.trim().length > 0 && { accountNumber: draft.accountNumber.trim() }),
  payoutMethod: 'ACH' as const,
  reasonCode,
  note,
  sendEmailNotification,
});

export const buildCheckPayload = (
  draft: CheckDraft,
  payoutMethod: PayoutMethod,
  sendEmailNotification: boolean,
  reasonCode?: string,
  note?: string,
) => ({
  line1: draft.line1.trim(),
  line2: draft.line2.trim() || undefined,
  city: draft.city.trim(),
  state: draft.state,
  zip: draft.zip.trim(),
  phoneNumber: draft.phone.trim(),
  payoutMethod,
  sendEmailNotification,
  ...(reasonCode && { reasonCode }),
  ...(note && { note }),
});

export const buildCheckUpdatePayload = (
  draft: CheckDraft,
  reasonCode: string,
  note: string,
  sendEmailNotification: boolean,
) => ({
  ...buildCheckPayload(draft, 'CHECK', sendEmailNotification),
  line2: draft.line2.trim() || null,
  reasonCode,
  note,
});

export const detectChangeType = (item: {
  updatedData: Record<string, unknown>;
}): PayoutMethod | null => {
  const keys = Object.keys(item.updatedData);

  if (
    keys.some((key) =>
      ['accountHolderName', 'accountType', 'routingNumber', 'accountNumber', 'accountNumberLast4'].includes(key),
    )
  ) {
    return 'ACH';
  }

  if (keys.some((key) => ['line1', 'line2', 'city', 'state', 'zip'].includes(key))) {
    return 'CHECK';
  }

  return null;
};

export const formatAddressSummary = (data: Record<string, unknown>): string => {
  const parts = [data.line1, data.line2, data.city, data.state, data.zip]
    .map(getTextValue)
    .filter(Boolean);

  return parts.length ? parts.join(', ') : '—';
};

export const formatAchSummary = (data: Record<string, unknown>): string => {
  const accountType = String(data.accountType ?? '').toLowerCase();
  const accountNumber = String(data.accountNumber ?? data.accountNumberLast4 ?? '');
  const routingNumber = String(data.routingNumber ?? '');
  const maskedAccountNumber = getMaskedAccountDisplay(accountNumber, accountNumber);
  const accountSummary = maskedAccountNumber || accountNumber;

  if (!accountType && !accountSummary && !routingNumber) return '—';

  return `${accountType} ${accountSummary}${routingNumber ? ` (rt ${routingNumber})` : ''}`.trim();
};

export const summariseHistoryValue = (
  data: Record<string, unknown>,
  changeType: PayoutMethod | null,
): string => {
  if (!Object.keys(data).length) return '—';
  if (changeType === 'ACH') return formatAchSummary(data);
  if (changeType === 'CHECK') return formatAddressSummary(data);

  return Object.values(data).filter(Boolean).join(', ');
};

export const formatReasonCode = (reasonCode?: string | null) =>
  reasonCode
    ? reasonCode
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/^\w/, (char) => char.toUpperCase())
    : null;

export const formatHistoryDate = (dateTime?: string | null) => {
  if (!dateTime) return null;

  try {
    return new Date(dateTime).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return dateTime;
  }
};
